// Agency Console theme registry — Console · Studio · HQ.
// A theme owns neutrals, surfaces, type, radii, shadows, and nav tone. It NEVER owns the accent:
// `--ss-accent` is the agency's white-label brand color (set from ss_agencies.accent) and every
// theme defers to it. "Your brand color, in the style you choose."
// Spec: docs/2026-08-05-agency-console-themes.md

export type ThemeId = 'console' | 'studio' | 'hq';

export interface FontSpec {
  family: string; // dedupe key, e.g. "Fraunces"
  href: string;   // Google Fonts stylesheet URL
}

export interface ThemeLayout {
  /** Studio: time-of-day greeting above the Help Desk section. */
  greeting: boolean;
}

export interface ThemeDefinition {
  id: ThemeId;
  meta: { name: string; description: string };
  cssVars: Record<string, string>;
  fonts: FontSpec[];
  layout: ThemeLayout;
}

const SANS = "-apple-system, 'Segoe UI', Roboto, sans-serif";

// ── Console — crisp & professional (Linear/Stripe lineage). Reproduces today's slate look,
//    so any unmigrated corner of the console still renders correctly under it.
const consoleTheme: ThemeDefinition = {
  id: 'console',
  meta: {
    name: 'Console',
    description: 'Crisp and professional. Quiet borders, tight type, your accent doing the talking.',
  },
  cssVars: {
    '--th-canvas': '#f8fafc',        // slate-50
    '--th-surface': '#ffffff',
    '--th-border': '#e2e8f0',        // slate-200
    '--th-ink': '#0f172a',           // slate-900
    '--th-muted': '#64748b',         // slate-500
    '--th-shadow-card': '0 1px 2px 0 rgb(0 0 0 / 0.05)',
    '--th-radius-card': '12px',
    '--th-font-body': `'Inter', ${SANS}`,
    '--th-font-display': `'Inter', ${SANS}`,
    '--th-nav-bg': '#ffffff',
    '--th-nav-ink': '#0f172a',
    '--th-nav-muted': '#64748b',
    '--th-nav-border': '#e2e8f0',
    '--th-nav-hover-bg': '#f1f5f9',
    '--th-nav-active-bg': 'var(--ss-accent, #1a73e8)',
    '--th-nav-active-fg': 'var(--ss-accent-fg, #ffffff)',
    '--th-page-title': '#0f172a',
  },
  fonts: [
    { family: 'Inter', href: 'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap' },
  ],
  layout: { greeting: false },
};

// ── Studio — warm & friendly (Intercom/HelpScout lineage).
const studioTheme: ThemeDefinition = {
  id: 'studio',
  meta: {
    name: 'Studio',
    description: 'Warm and friendly. Soft cards, a serif voice, and a greeting that talks to you.',
  },
  cssVars: {
    '--th-canvas': '#faf6f0',
    '--th-surface': '#ffffff',
    '--th-border': '#efe8dd',
    '--th-ink': '#34302a',
    '--th-muted': '#8a8378',
    '--th-shadow-card': '0 6px 24px rgba(90,70,40,0.06)',
    '--th-radius-card': '18px',
    '--th-font-body': `'Nunito Sans', ${SANS}`,
    '--th-font-display': "'Fraunces', Georgia, 'Times New Roman', serif",
    '--th-nav-bg': '#ffffff',
    '--th-nav-ink': '#34302a',
    '--th-nav-muted': '#8a8378',
    '--th-nav-border': '#efe8dd',
    '--th-nav-hover-bg': '#faf6f0',
    '--th-nav-active-bg': 'color-mix(in srgb, var(--ss-accent, #1a73e8) 10%, #ffffff)',
    '--th-nav-active-fg': 'var(--ss-accent, #1a73e8)',
    '--th-page-title': '#34302a',
  },
  fonts: [
    { family: 'Nunito Sans', href: 'https://fonts.googleapis.com/css2?family=Nunito+Sans:opsz,wght@6..12,400;6..12,600;6..12,700&display=swap' },
    { family: 'Fraunces', href: 'https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,400;9..144,500;9..144,600&display=swap' },
  ],
  layout: { greeting: true },
};

// ── HQ — bold & branded. Dark navy gradient SIDEBAR (mission control), light content.
const hqTheme: ThemeDefinition = {
  id: 'hq',
  meta: {
    name: 'HQ',
    description: 'Bold and branded. A deep navy rail with display type — mission control.',
  },
  cssVars: {
    '--th-canvas': '#edf2fa',
    '--th-surface': '#ffffff',
    '--th-border': '#dfe6f2',
    '--th-ink': '#12213f',
    '--th-muted': '#7a87a0',
    '--th-shadow-card': '0 12px 40px rgba(10,30,68,0.10)',
    '--th-radius-card': '16px',
    '--th-font-body': `'Inter', ${SANS}`,
    '--th-font-display': `'Space Grotesk', 'Inter', ${SANS}`,
    '--th-nav-bg': 'linear-gradient(170deg, #0a1e44 0%, #10307a 60%, color-mix(in srgb, var(--ss-accent, #1e5ee0) 55%, #10307a) 100%)',
    '--th-nav-ink': '#ffffff',
    '--th-nav-muted': 'rgba(255,255,255,0.62)',
    '--th-nav-border': 'rgba(255,255,255,0.12)',
    '--th-nav-hover-bg': 'rgba(255,255,255,0.08)',
    '--th-nav-active-bg': 'rgba(255,255,255,0.16)',
    '--th-nav-active-fg': '#ffffff',
    '--th-page-title': '#12213f',
  },
  fonts: [
    { family: 'Inter', href: 'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap' },
    { family: 'Space Grotesk', href: 'https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;600;700&display=swap' },
  ],
  layout: { greeting: false },
};

export const THEMES: Record<ThemeId, ThemeDefinition> = {
  console: consoleTheme,
  studio: studioTheme,
  hq: hqTheme,
};

export const THEME_IDS = Object.keys(THEMES) as ThemeId[];
export const DEFAULT_THEME: ThemeId = 'console';

export function getTheme(id: string | null | undefined): ThemeDefinition {
  if (id && id in THEMES) return THEMES[id as ThemeId];
  return THEMES[DEFAULT_THEME];
}

const STORAGE_KEY = 'ss_agency_theme';

// The shell must re-render when the theme changes (layout flags flip sidebar↔topbar), so
// applyTheme notifies subscribers. Dependency-free substitute for a context provider.
type ThemeListener = (theme: ThemeDefinition) => void;
const listeners = new Set<ThemeListener>();

export function onThemeChange(cb: ThemeListener): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

/** Cached theme id for flash-free first paint (before settings load). */
export function cachedThemeId(): ThemeId {
  try {
    return getTheme(localStorage.getItem(STORAGE_KEY)).id;
  } catch {
    return DEFAULT_THEME;
  }
}

/** Set the vars on <html>, stamp data-theme, lazy-load fonts, remember the choice. */
export function applyTheme(idOrTheme: string | ThemeDefinition): ThemeDefinition {
  const theme = typeof idOrTheme === 'string' ? getTheme(idOrTheme) : idOrTheme;
  const root = document.documentElement;
  for (const [k, v] of Object.entries(theme.cssVars)) root.style.setProperty(k, v);
  root.dataset.theme = theme.id;
  for (const font of theme.fonts) {
    const id = `theme-font-${font.family.toLowerCase().replace(/\s+/g, '-')}`;
    if (!document.getElementById(id)) {
      const link = document.createElement('link');
      link.id = id;
      link.rel = 'stylesheet';
      link.href = font.href;
      document.head.appendChild(link);
    }
  }
  try {
    localStorage.setItem(STORAGE_KEY, theme.id);
  } catch {
    // private mode etc. — cache is an optimization only
  }
  for (const cb of listeners) cb(theme);
  return theme;
}
